/*
  The map pin in a pasted link (03 #10) — the same reading as the server's backend/project_geo.py
  (pin_from_link, route_start_only, in_service_area); keep the two in step (both have tests on the same links).

  A directions link gives its DESTINATION, never the point the route starts from: Waze `to=ll.…` but not
  `from=ll.…`, Google `destination=` / `daddr=` but not `origin=` / `saddr=`, and the last stop of a
  /maps/dir/<start>/<destination>/ route. lib/waze.js (shared, older) still reads `from=` — the Projects pages
  use this file instead.
*/

export const LINK_MAX_LENGTH = 2000;

// "24.53,54.40" or Waze's "ll.24.53,54.40"; both numbers need decimals (whole numbers in a link are house
// numbers or zoom levels). Same pattern as project_geo._PAIR.
const PAIR = /(?:ll[.:])?(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)/i;
// Query parameters that hold the place itself; never from= / saddr= / origin= (where the driver starts).
const PLACE_PARAMS = ['ll', 'to', 'q', 'query', 'destination', 'daddr', 'center'];
const START_PARAMS = ['from', 'saddr', 'origin'];

/** The Gulf — the UAE, Saudi Arabia and their neighbours: [south, north, west, east] (project_geo.SERVICE_AREA). */
export const SERVICE_AREA = [16, 33, 34, 60];

const blank = (value) => value === null || value === undefined || String(value).trim() === '';

/** { lat, lng } rounded to 7 places when both are real coordinates; null otherwise (0, 0 is "no pin"). */
export function toPin(latitude, longitude) {
  if (blank(latitude) || blank(longitude)) return null;
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return null;
  return { lat: Number(lat.toFixed(7)), lng: Number(lng.toFixed(7)) };
}

function pairIn(text) {
  const match = String(text || '').trim().match(PAIR);
  return match ? toPin(match[1], match[2]) : null;
}

function urlOf(raw) {
  try {
    const url = new URL(raw);
    return url.protocol && (url.host || url.pathname) ? url : null;
  } catch {
    return null;
  }
}

function pathText(url) {
  let path = url.pathname;
  try {
    path = decodeURIComponent(path);
  } catch {
    // a stray "%" — read the path as it is
  }
  return path.replace(/\+/g, ' ');
}

/** The stops of a Google Maps route (/maps/dir/<start>/…/<destination>/@<view>/data=…); null when not a route. */
function routeOf(url) {
  const segments = pathText(url).split('/');
  const at = segments.indexOf('dir');
  if (at < 0) return null;
  const route = segments.slice(at + 1);
  const cut = route.findIndex((segment) => segment.startsWith('@') || segment.startsWith('data='));
  if (cut >= 0) return route.slice(0, cut);
  return route.length && !route[route.length - 1].trim() ? route.slice(0, -1) : route; // the trailing slash
}

const firstParam = (url, names) => {
  for (const name of names) {
    const value = url.searchParams.get(name);
    if (value) return value;
  }
  return null;
};

/** { lat, lng } of the place a map link points at (see the top of the file), or null. */
export function pinFromLink(link) {
  const raw = String(link ?? '').trim();
  if (!raw || raw.length > LINK_MAX_LENGTH) return null;
  const url = urlOf(raw);
  if (!url) return pairIn(raw);
  const direct = toPin(firstParam(url, ['lat', 'latitude']), firstParam(url, ['lon', 'lng', 'longitude']));
  if (direct) return direct;
  for (const name of PLACE_PARAMS) {
    const found = pairIn(url.searchParams.get(name));
    if (found) return found;
  }
  const route = routeOf(url);
  if (route) return route.length >= 2 ? pairIn(route[route.length - 1]) : null;
  return pairIn(pathText(url));
}

/** A directions link whose only coordinates are where the route STARTS — it gives no pin; say why. */
export function routeStartOnly(link) {
  const raw = String(link ?? '').trim();
  if (!raw || raw.length > LINK_MAX_LENGTH || pinFromLink(raw)) return false;
  const url = urlOf(raw);
  if (!url) return false;
  if (START_PARAMS.some((name) => pairIn(url.searchParams.get(name)))) return true;
  const route = routeOf(url) || [];
  const starts = route.length >= 2 ? route.slice(0, -1) : route;
  return starts.some((stop) => pairIn(stop));
}

export function inServiceArea(pin) {
  if (!pin) return false;
  const [south, north, west, east] = SERVICE_AREA;
  return pin.lat >= south && pin.lat <= north && pin.lng >= west && pin.lng <= east;
}
